/**
 * @file Header.jsx
 * @description Segmented navigation header. The desktop nav renders each route
 *   as an independent floating chip connected by circuit-wire connectors.
 *   Supports dropdown sub-menus, smooth section scrolling with cross-route
 *   persistence, and a full-screen mobile panel.
 * @author IEEE CIS UJ Web Team
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedinIn, faInstagram, faFacebookF, faYoutube } from '@fortawesome/free-brands-svg-icons';
import '../ComponentsStyling/Header.css';
import { CHAPTER_SOCIAL_LINKS } from '../socialLinks';

const logo = '/images/webp/branding/IEEE-Logo-base1-png.webp';

// Sub-item definitions for each dropdown-enabled navigation segment.
const teamMenu = [{ label: 'Leadership', id: 'leadership' }, { label: 'Team Section', id: 'team-section' }, { label: 'Stars of Month', id: 'stars-of-month' }, { label: 'Gallery', id: 'gallery' }];
const aboutMenu = [
    { label: 'Mission & Vision', id: 'mission-vision' },
    { label: 'Goals & Objectives', id: 'goals' },
    { label: 'Achievements', id: 'achievements' },
    { label: 'Mascot', id: 'mascot' },
    { label: 'Latest News', id: 'blog' },
    { label: 'Privacy & Policy', id: 'policy' },
];
const partnersMenu = [{ label: 'Strategic Partners', id: 'strategic-partners' }, { label: 'Advisors', id: 'advisors' }];

const socialLinks = [
    { href: CHAPTER_SOCIAL_LINKS.linkedin, icon: faLinkedinIn, label: 'LinkedIn' },
    { href: CHAPTER_SOCIAL_LINKS.instagram, icon: faInstagram, label: 'Instagram' },
    { href: CHAPTER_SOCIAL_LINKS.facebook, icon: faFacebookF, label: 'Facebook' },
    { href: '/youtube', icon: faYoutube, label: 'YouTube', internal: true },
];


// Inline SVG icon components for each navigation segment.
const IcoHome = () => <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5L11 3l8 7.5V19a1 1 0 01-1 1H4a1 1 0 01-1-1v-8.5z" /><path d="M8 20v-7h6v7" /></svg>;
const IcoAbout = () => <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><circle cx="11" cy="8" r="1.2" fill="currentColor" stroke="none" /><path d="M11 11.5v5" /></svg>;
const IcoPartners = () => <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="3.2" /><path d="M2 19c0-3.2 2.7-5.5 6-5.5s6 2.3 6 5.5" /><path d="M15 5a3.2 3.2 0 010 6.4" /><path d="M20 19c0-2.8-2-4.8-5-5.3" /></svg>;
const IcoTeams = () => <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="7.5" r="3.8" /><path d="M3 20a8 8 0 0116 0" /></svg>;
const IcoEvents = () => <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="16" height="16" rx="2.5" /><path d="M3 9h16M8 2v3M14 2v3" /><circle cx="8" cy="13.5" r="1" fill="currentColor" stroke="none" /><circle cx="11" cy="13.5" r="1" fill="currentColor" stroke="none" /><circle cx="14" cy="13.5" r="1" fill="currentColor" stroke="none" /><circle cx="8" cy="17" r="1" fill="currentColor" stroke="none" /><circle cx="11" cy="17" r="1" fill="currentColor" stroke="none" /></svg>;
const IcoFaq = () => <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="M8.6 8.8a2.4 2.4 0 014.8 0c0 1.5-1.6 2.1-2.4 2.8-.5.4-.8.9-.8 1.6" /><circle cx="11" cy="15.9" r="0.9" fill="currentColor" stroke="none" /></svg>;
const IcoIEEE = () => <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><ellipse cx="11" cy="11" rx="3.5" ry="8" /><path d="M3.2 8h15.6M3.2 14h15.6" /></svg>;

// IEEE official resources dropdown menu items
const ieeeMenu = [
    { label: 'IEEE.org', id: 'ieee-org', href: 'https://www.ieee.org/' },
    { label: 'IEEE Xplore', id: 'ieee-xplore', href: 'https://ieeexplore.ieee.org/' },
    { label: 'IEEE Spectrum', id: 'ieee-spectrum', href: 'https://spectrum.ieee.org/' },
];

const IcoStore = () => (
    <svg viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="7" width="16" height="10" rx="2.5" />
        <path d="M7 7V5a4 4 0 0 1 8 0v2" />
        <path d="M9 11h4" />
    </svg>
);

const NAV = [
    { key: 'home', label: 'HOME', Icon: IcoHome, path: '/', menu: null, target: null },
    { key: 'about', label: 'ABOUT', Icon: IcoAbout, path: '/', menu: aboutMenu, target: 'home' },
    { key: 'partners', label: 'PARTNERS', Icon: IcoPartners, path: '/partners', menu: partnersMenu, target: 'partners' },
    { key: 'teams', label: 'TEAMS', Icon: IcoTeams, path: '/teams', menu: teamMenu, target: 'teams' },
    { key: 'faq', label: 'FAQ', Icon: IcoFaq, path: '/faq', menu: null, target: null },
    { key: 'events', label: 'EVENTS', Icon: IcoEvents, path: '/events', menu: null, target: null },
    { key: 'store', label: 'STORE', Icon: IcoStore, path: '/store', menu: null, target: null },
    { key: 'ieee-org', label: 'IEEE ORG', Icon: IcoIEEE, path: null, menu: ieeeMenu, target: null, external: true },
];

/**
 * Dropdown panel rendered beneath a nav chip when the segment is hovered.
 * Fires onSelect with the target section ID when an item is clicked.
 *
 * @param {Array}    items   - Menu item definitions ({ label, id }).
 * @param {boolean}  visible - Whether the panel is currently open.
 * @param {Function} onSelect - Callback invoked with the selected section id.
 */
const DropPanel = ({ items, visible, onSelect }) => (
    <div className={`hdr-drop${visible ? ' hdr-drop--open' : ''}`} role="menu">
        <div className="hdr-drop__topline" aria-hidden />
        <div className="hdr-drop__scan" aria-hidden />
        <div className="hdr-drop__corners" aria-hidden><span /><span /><span /><span /></div>
        {items.map((item, i) => (
            item.href ? (
                <a key={item.id} href={item.href} target="_blank" rel="noopener noreferrer"
                    className="hdr-drop__row" style={{ '--i': i }} role="menuitem">
                    <span className="hdr-drop__pip" aria-hidden />
                    <span className="hdr-drop__label">{item.label}</span>
                    <span className="hdr-drop__arrow" aria-hidden>›</span>
                </a>
            ) : (
                <button key={item.id} className="hdr-drop__row" style={{ '--i': i }} onClick={() => onSelect(item.id)} role="menuitem">
                    <span className="hdr-drop__pip" aria-hidden />
                    <span className="hdr-drop__label">{item.label}</span>
                    <span className="hdr-drop__arrow" aria-hidden>›</span>
                </button>
            )
        ))}
        <div className="hdr-drop__id" aria-hidden>CIS·NODE·{String(Math.floor(Math.random() * 9000) + 1000)}</div>
    </div>
);

/**
 * Site-wide navigation header. Handles scroll-aware styling, cross-route
 * section scrolling via sessionStorage, mobile panel toggling, and dropdown
 * hover state with debounced close timers.
 */
const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [openDrop, setOpenDrop] = useState(null);
    const [mobExpand, setMobExpand] = useState(null);
    const timers = useRef({});

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    useEffect(() => {
        setMobileOpen(false); setOpenDrop(null);
    }, [location.pathname]);

    const hover = key => { clearTimeout(timers.current[key]); setOpenDrop(key); };
    const unhover = key => { timers.current[key] = setTimeout(() => setOpenDrop(d => d === key ? null : d), 180); };

    const goSection = useCallback((id, target) => {
        setMobileOpen(false); setOpenDrop(null);

        if (id === 'policy') {
            navigate('/privacy-policy');
            return;
        }

        const route = target === 'partners' ? '/partners' : target === 'teams' ? '/teams' : target === 'events' ? '/events' : '/';

        if (location.pathname !== route) { sessionStorage.setItem('scrollToSection', id); navigate(route); return; }
        const el = document.getElementById(id);
        if (el) {
            const h = document.querySelector('.site-header')?.offsetHeight || 80;
            window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - h - 16, behavior: 'smooth' });
        }
    }, [location.pathname, navigate]);

    const isActive = ({ key }) => {
        if (key === 'events') return location.pathname === '/events' || location.pathname === '/all-events';
        if (key === 'partners') return location.pathname === '/partners';
        if (key === 'teams') return location.pathname === '/teams';
        if (key === 'faq') return location.pathname === '/faq';
        if (key === 'join') return location.pathname === '/join';
        if (key === 'home') return location.pathname === '/';
        return false;
    };

    return (
        <>
            {mobileOpen && <div className="hdr-overlay" onClick={() => setMobileOpen(false)} aria-hidden />}

            <header className={`site-header${scrolled ? ' site-header--scrolled' : ''}`}>

                {/* Logo with animated pulse indicator and chapter tag. */}
                <Link to="/" className="hdr-logo" aria-label="IEEE CIS UJ">
                    <img src={logo} alt="IEEE CIS UJ" className="hdr-logo__img" decoding="async" fetchPriority="high" />
                    <div className="hdr-logo__info" aria-hidden>
                        <span className="hdr-logo__pulse" />
                        <span className="hdr-logo__tag">CIS · UJ</span>
                    </div>
                </Link>

                {/* Segmented navigation strip. */}
                <nav className="hdr-strip" aria-label="Main navigation">

                    {/* Ambient radial glow rendered behind the full nav strip. */}
                    <div className="hdr-strip__ambient" aria-hidden />

                    {NAV.map((item, idx) => {
                        const active = isActive(item);
                        const dropped = openDrop === item.key;
                        const hasDrop = !!item.menu;

                        return (
                            <React.Fragment key={item.key}>
                                {/* Circuit-wire connector rendered between adjacent segments. */}
                                {idx > 0 && (
                                    <div className="hdr-wire" aria-hidden>
                                        <div className="hdr-wire__line" />
                                        <div className="hdr-wire__node" />
                                    </div>
                                )}

                                {/* Nav segment chip. */}
                                <div
                                    className="hdr-seg"
                                    onMouseEnter={() => hasDrop && hover(item.key)}
                                    onMouseLeave={() => hasDrop && unhover(item.key)}
                                >
                                    {hasDrop ? (
                                        <button
                                            className={`hdr-chip${active ? ' hdr-chip--active' : ''}${dropped ? ' hdr-chip--open' : ''}`}
                                            aria-haspopup="true"
                                            aria-expanded={dropped}
                                            aria-label={item.label}
                                            onClick={() => setOpenDrop(prev => prev === item.key ? null : item.key)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Escape') {
                                                    setOpenDrop(null);
                                                }
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setOpenDrop(prev => prev === item.key ? null : item.key);
                                                }
                                                if (e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                    setOpenDrop(item.key);
                                                }
                                            }}
                                        >
                                            {active && <span className="hdr-chip__glow" aria-hidden />}
                                            <span className="hdr-chip__icon"><item.Icon /></span>
                                            <span className="hdr-chip__label">{item.label}</span>
                                            {hasDrop && (
                                                <svg className={`hdr-chip__chev${dropped ? ' hdr-chip__chev--open' : ''}`} viewBox="0 0 8 5" fill="none" aria-hidden>
                                                    <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                                </svg>
                                            )}
                                        </button>
                                    ) : item.key === 'home' ? (
                                        <button
                                            className={`hdr-chip${active ? ' hdr-chip--active' : ''}`}
                                            onClick={() => goSection('home', null)}
                                            aria-label={item.label}
                                        >
                                            {active && <span className="hdr-chip__glow" aria-hidden />}
                                            <span className="hdr-chip__icon"><item.Icon /></span>
                                            <span className="hdr-chip__label">{item.label}</span>
                                        </button>
                                    ) : (
                                        <Link
                                            to={item.path}
                                            className={`hdr-chip${active ? ' hdr-chip--active' : ''}`}
                                            aria-label={item.label}
                                        >
                                            {active && <span className="hdr-chip__glow" aria-hidden />}
                                            <span className="hdr-chip__icon"><item.Icon /></span>
                                            <span className="hdr-chip__label">{item.label}</span>
                                        </Link>
                                    )}

                                    {hasDrop && (
                                        <DropPanel items={item.menu} visible={dropped} onSelect={id => goSection(id, item.target)} />
                                    )}
                                </div>
                            </React.Fragment>
                        );
                    })}
                </nav>

                {/* Right-side action area: social links, Join CTA, and mobile burger. */}
                <div className="hdr-right">
                    <div className="hdr-socials">
                        {socialLinks.map(({ href, icon, label, internal }) => (
                            internal ? (
                                <Link key={label} to={href} className="hdr-social" aria-label={label}>
                                    <FontAwesomeIcon icon={icon} />
                                </Link>
                            ) : (
                                <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="hdr-social" aria-label={label}>
                                    <FontAwesomeIcon icon={icon} />
                                </a>
                            )
                        ))}
                    </div>
                    <Link to="/join" className="hdr-join" onClick={() => setMobileOpen(false)}>
                        <span className="hdr-join__bracket" aria-hidden>[</span>
                        <span className="hdr-join__text">JOIN</span>
                        <span className="hdr-join__bracket" aria-hidden>]</span>
                    </Link>
                    <button
                        className={`hdr-burger${mobileOpen ? ' hdr-burger--open' : ''}`}
                        onClick={() => setMobileOpen(v => !v)}
                        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={mobileOpen}
                    >
                        <span /><span /><span />
                    </button>
                </div>

            </header>

            {/* Full-screen mobile navigation panel. */}
            <aside className={`hdr-mobile${mobileOpen ? ' hdr-mobile--open' : ''}`} aria-hidden={!mobileOpen}>

                {/* Close button for the mobile panel. */}
                <button
                    className="hdr-mobile__close"
                    onClick={() => setMobileOpen(false)}
                    aria-label="Close menu"
                >
                    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                        <path d="M1 1l12 12M13 1L1 13" />
                    </svg>
                </button>

                <div className="hdr-mobile__topbar" aria-hidden>
                    <span className="hdr-mobile__sys">IEEE · CIS · UJ</span>
                    <span className="hdr-mobile__live"><span className="hdr-mobile__dot" />LIVE</span>
                </div>

                <ul className="hdr-mobile__list">
                    {NAV.map((item, i) => (
                        <li key={item.key} className="hdr-mobile__item" style={{ '--mi': i }}>
                            {item.menu ? (
                                <>
                                    <button
                                        className={`hdr-mobile__link hdr-mobile__link--drop${isActive(item) ? ' hdr-mobile__link--on' : ''}`}
                                        onClick={() => setMobExpand(v => v === item.key ? null : item.key)}
                                        aria-expanded={mobExpand === item.key}
                                    >
                                        <span className="hdr-mobile__idx">0{i + 1}</span>
                                        <span className="hdr-mobile__mico"><item.Icon /></span>
                                        <span className="hdr-mobile__mlabel">{item.label}</span>
                                        <svg className={`hdr-mobile__mchev${mobExpand === item.key ? ' open' : ''}`} viewBox="0 0 10 6" fill="none">
                                            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                        </svg>
                                    </button>
                                    <ul className={`hdr-mobile__sub${mobExpand === item.key ? ' hdr-mobile__sub--open' : ''}`}> 
                                        {item.menu.map(m => (
                                            <li key={m.id}>
                                                {m.href ? (
                                                    <a
                                                        href={m.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="hdr-mobile__sublink"
                                                    >
                                                        <span className="hdr-mobile__subdot" aria-hidden />
                                                        {m.label}
                                                    </a>
                                                ) : (
                                                    <button className="hdr-mobile__sublink" onClick={() => goSection(m.id, item.target)}>
                                                        <span className="hdr-mobile__subdot" aria-hidden />
                                                        {m.label}
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : item.key === 'home' ? (
                                <button
                                    className={`hdr-mobile__link${isActive(item) ? ' hdr-mobile__link--on' : ''}`}
                                    onClick={() => goSection('home', null)}
                                >
                                    <span className="hdr-mobile__idx">0{i + 1}</span>
                                    <span className="hdr-mobile__mico"><item.Icon /></span>
                                    <span className="hdr-mobile__mlabel">{item.label}</span>
                                </button>
                            ) : (
                                <Link
                                    to={item.path}
                                    className={`hdr-mobile__link${isActive(item) ? ' hdr-mobile__link--on' : ''}`}
                                    onClick={() => setMobileOpen(false)}
                                >
                                    <span className="hdr-mobile__idx">0{i + 1}</span>
                                    <span className="hdr-mobile__mico"><item.Icon /></span>
                                    <span className="hdr-mobile__mlabel">{item.label}</span>
                                </Link>
                            )}
                        </li>
                    ))}
                </ul>
                {/* Footer with join button and social icons removed for mobile */}
            </aside>
        </>
    );
};

export default Header;